import React, { useState } from 'react';
import { ProhibitedRegular, ArrowLeftRegular } from '@fluentui/react-icons';
import {
  Button,
  FluentProvider,
  webLightTheme,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';
import { useHistory, useParams } from 'react-router-dom';
import { ICompanyCreationParams } from '../../common/ParamTypes';
import PivotHeader from '../../common/headers/PivotHeader';
import { IPivotHeaderItems } from '../../../utils/types/IPivotHeaderItems';
import {
  bodyContentContainer,
  iconButtonStyles,
} from '../../../app/common/styles/CommonStyleObjects';
import CanAccess from '../../common/canAccess/CanAccess';
import DeleteConfirmationDialog from '../../common/DeleteConfirmationDialog';

interface IHeaderProps {
  showBackToListButton?: boolean;
  canCancelRequest?: boolean;
  headerText: string;
  onCancelClick: () => void;
  showDocumentsButton?: boolean;
}

const Header = (props: IHeaderProps): JSX.Element => {
  const { showBackToListButton, canCancelRequest, headerText, onCancelClick, showDocumentsButton } =
    props;
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { id } = useParams<ICompanyCreationParams>();
  const history = useHistory();

  const handleCancelRequest = () => {
    onCancelClick();
    setShowDeleteConfirmation(false);
  };

  const HeaderItems: IPivotHeaderItems = showDocumentsButton
    ? {
        overview: {
          headerText: 'Overview',
          iconName: 'ServerProcesses',
        },
        'company-information': {
          headerText: 'Company Information',
          iconName: 'ServerProcesses',
        },
        documents: {
          headerText: 'Documents',
          iconName: 'People',
        },
      }
    : {
        overview: {
          headerText: 'Overview',
          iconName: 'ServerProcesses',
        },
        'company-information': {
          headerText: 'Company Information',
          iconName: 'ServerProcesses',
        },
      };

  const renderHeaderTitle = (): JSX.Element => {
    if (headerText) {
      return (
        <h1>
          {showBackToListButton && (
            <Button
              appearance="subtle"
              icon={<ArrowLeftRegular color={iconButtonStyles.color} />}
              style={{ marginRight: 10 }}
              title="Back to company request list"
              aria-label="Back to company request list"
              onClick={(): void => {
                history.push(`/company-creation/`);
              }}
            />
          )}

          {`${headerText}`}
        </h1>
      );
    }
    return (
      <Skeleton aria-label="Loading company creation header">
        <SkeletonItem />
      </Skeleton>
    );
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <PivotHeader
        getHeaderTitle={renderHeaderTitle}
        pivotHeaderItems={HeaderItems}
        baseUri={`/company-creation/${id}`}
        ariaLabel="Navigation for Company Creation Details"
      />
      <CanAccess requestedAction="CompanyCreation:Cancel">
        <Button
          appearance="transparent"
          id="DisabledNewExtensionButton"
          style={{ alignSelf: 'flex-end', marginRight: 38 }}
          className={`${bodyContentContainer}`}
          disabled={!canCancelRequest}
          icon={<ProhibitedRegular color={iconButtonStyles.color} />}
          onClick={() => {
            setShowDeleteConfirmation(true);
          }}
        >
          Cancel Request
        </Button>
      </CanAccess>

      {showDeleteConfirmation && (
        <FluentProvider theme={webLightTheme}>
          <DeleteConfirmationDialog
            open={showDeleteConfirmation}
            message="Do you want to cancel this company creation request?"
            onConfirm={handleCancelRequest}
            onCancel={() => setShowDeleteConfirmation(false)}
          />
        </FluentProvider>
      )}
    </div>
  );
};

export default Header;
